import React, { useState, useEffect } from 'react';
import './Upgrade.css';
import { useNavigate, Link } from 'react-router-dom';
import { BsClock, BsX } from 'react-icons/bs';
import api from '../../utils/axios';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import Navbar from '../../components/Navbar';

const packages = [
    {
        id: 4,
        minutes: 'Unlimited',
        title: 'Pro Weekly Subscription',
        description: 'Unlimited minutes per week. Perfect for consistent practice and improvement.',
        price: '$10/week',
        type: 'subscription',
        featured: true
    },
    {
        id: 1,
        minutes: 15,
        title: 'Focus 15: Quick Tuner',
        description: 'Quick 15-minute practice session',
        price: '$1.99',
        type: 'one-time'
    },
    {
        id: 2,
        minutes: 30,
        title: 'Half-Hour Impact',
        description: '30 focused practice minutes',
        price: '$2.99',
        type: 'one-time'
    },
    {
        id: 3,
        minutes: 60,
        title: 'Hour of Mastery',
        description: 'Full hour of practice time',
        price: '$4.99',
        type: 'one-time'
    }
];

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#fff',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
};

function Upgrade() {
    const navigate = useNavigate();
    const [minutesRemaining, setMinutesRemaining] = useState(null);
    const [dailyMinuteLimit, setDailyMinuteLimit] = useState(60);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        const fetchRemainingMinutes = async () => {
            try {
                const response = await api.get('/recording/minutes-remaining');
                const minutes = response.data.minutesRemaining;
                setMinutesRemaining(minutes !== null && minutes !== undefined ? minutes : 0);
            } catch (error) {
                console.error('Error fetching remaining minutes:', error);
                setMinutesRemaining(0); // Default to 0 in case of error
            }
        };

        const fetchDailyMinuteLimit = async () => {
            try {
                const response = await api.get('/settings/daily-minute-limit');
                setDailyMinuteLimit(parseFloat(response.data.dailyMinuteLimit) || 60);
            } catch (error) {
                console.error('Error fetching daily minute limit:', error);
            }
        };

        fetchRemainingMinutes();
        fetchDailyMinuteLimit();
    }, []);

    const handlePurchaseClick = (pkg) => {
        setSelectedPackage(pkg);
        setShowPaymentModal(true);
    };

    const handlePurchase = async (e) => {
        e.preventDefault();
        try {
            setError('');
            setIsLoading(true);

            if (!stripe || !elements) {
                throw new Error('Stripe has not been initialized.');
            }

            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('Card element not found.');
            }

            if (selectedPackage.type === 'subscription') {
                const { data } = await api.post('/upgrade/subscribe');
                const result = await stripe.confirmCardPayment(data.clientSecret, {
                    payment_method: {
                        card: cardElement,
                    }
                });

                if (result.error) {
                    setError(result.error.message);
                } else {
                    setPaymentSuccess(true);
                }
            } else {

                const { data } = await api.post('/upgrade/purchase', { packageId: selectedPackage.id });

                if (!data.clientSecret) {
                    throw new Error('Payment initialization failed. Please try again.');
                }

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    throw new Error('Card element not found.');
                }

                const result = await stripe.confirmCardPayment(data.clientSecret, {
                    payment_method: {
                        card: cardElement,
                    }
                });

                if (result.error) {
                    setError(result.error.message || 'Payment failed. Please try again.');
                } else if (result.paymentIntent.status === 'succeeded') {
                    setError('');
                    setPaymentSuccess(true);
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
            setError(errorMessage);
            console.error('Purchase error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="upgrade-page">
                {error && (
                    <div className="error-message" role="alert">
                        {error}
                    </div>
                )}
                <div className="minutes-counter">
                    <BsClock /> <span>{minutesRemaining !== null ? `${minutesRemaining.toFixed(0)} Practice Minutes Remaining Today` : 'Loading...'}</span>
                </div>
                <h1>Choose Your Plan</h1>
                <div className="packages">
                    {packages.filter(pkg => pkg.featured).map((pkg) => (
                        <div key={pkg.id} className="package-card featured">
                            <h2>{pkg.title}</h2>
                            <p>{pkg.description}</p>
                            <div className="package-details">
                                <span className="package-price">{pkg.price}</span>
                                <span className="package-minutes">
                                    <BsClock /> {pkg.minutes} minutes
                                </span>
                            </div>
                            <button onClick={() => handlePurchaseClick(pkg)}>
                                Start Your Pro Subscription
                            </button>
                        </div>
                    ))}

                    <div className="one-time-packages">
                        {packages.filter(pkg => !pkg.featured).map((pkg) => (
                            <div key={pkg.id} className="package-card">
                                <h2>{pkg.title}</h2>
                                <p>{pkg.description}</p>
                                <div className="package-details">
                                    <span className="package-price">{pkg.price}</span>
                                    <span className="package-minutes">
                                        <BsClock /> {pkg.minutes} minutes
                                    </span>
                                </div>
                                <button onClick={() => handlePurchaseClick(pkg)}>
                                    Purchase Credits
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="disclaimer">
                    <p>Note: Minutes reset daily at midnight EST</p>
                </div>
                {showPaymentModal && (
                    <div className="modal-overlay">
                        <div className="payment-modal">
                            {paymentSuccess ? (
                                <div className="success-message">
                                    <h2>Thank you for your purchase!</h2>
                                    <p>Your {selectedPackage.minutes} minutes have been added to your account.</p>
                                    <Link to="/" className="return-home">Return to Dashboard</Link>
                                </div>
                            ) : (
                                <>
                                    <button className="close-modal" onClick={() => setShowPaymentModal(false)}>
                                        <BsX />
                                    </button>
                                    <h2>Complete Purchase</h2>
                                    <div className="purchase-summary">
                                        <div className="summary-item">
                                            <span>Package</span>
                                            <span>{selectedPackage?.title}</span>
                                        </div>
                                        <div className="summary-item">
                                            <span>Minutes</span>
                                            <span>{selectedPackage?.minutes} minutes</span>
                                        </div>
                                        <div className="summary-item">
                                            <span>Subtotal</span>
                                            <span>CAD {selectedPackage?.price.replace('$', '')}</span>
                                        </div>
                                        <div className="summary-item">
                                            <span>Tax</span>
                                            <span>CAD $0.00</span>
                                        </div>
                                        <div className="summary-item total">
                                            <span>Total</span>
                                            <span>CAD {selectedPackage?.price.replace('$', '')}</span>
                                        </div>
                                    </div>
                                    <div className="secure-badge">
                                        <span>🔒 Secure Payment</span>
                                        <p>Your payment is processed securely through Stripe</p>
                                    </div>
                                    <form onSubmit={handlePurchase}>
                                        <label className="card-label">Credit or debit card</label>
                                        <div className="card-element-container">
                                            <CardElement options={CARD_ELEMENT_OPTIONS} />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className={`pay-button ${isLoading ? 'loading' : ''}`}
                                        >
                                            {isLoading ? 'Processing...' : `Pay CAD ${selectedPackage?.price.replace('$', '')}`}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default Upgrade;